/**
 * Geometry for the editor tour's spotlight and card.
 *
 * Deliberately pure, dependency-free and closure-free: every function is
 * self-contained so it can be injected into a real browser and asserted against a
 * real layout engine. jsdom cannot help here, it has no layout and returns zeroed
 * rects, so exactness is only provable in a real engine.
 *
 * See `e2e/optidev/08-onboarding/tour-spotlight-geometry.spec.ts`.
 */

export interface AnchorRect {
  top: number;
  left: number;
  width: number;
  height: number;
  /** Four corner radii, in CSS order, ready to assign to `border-radius`. */
  radius: string;
}

export interface CardPlacement {
  top: number;
  left: number;
}

export type TourSide = "right" | "left" | "below";

/**
 * Read an anchor's exact box and its exact corner radii.
 *
 * `getBoundingClientRect` is the right source: it returns sub-pixel values and
 * already accounts for any transform on an ancestor, which matters because the
 * preview sits inside a zoom/pan wrapper. Values are returned unrounded, because
 * rounding is precisely what leaves an overlay sitting a hair off its target.
 *
 * Radius is read per corner rather than from the `borderRadius` shorthand, whose
 * computed value collapses to a single figure on some engines and would square off
 * a control with mixed corners.
 */
export function readAnchorRect(el: Element): AnchorRect | null {
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return null;

  const cs = window.getComputedStyle(el);
  const radius = [
    cs.borderTopLeftRadius,
    cs.borderTopRightRadius,
    cs.borderBottomRightRadius,
    cs.borderBottomLeftRadius,
  ]
    .map((v) => v || "0px")
    .join(" ");

  return {
    top: r.top,
    left: r.left,
    width: r.width,
    height: r.height,
    radius,
  };
}

/**
 * Sub-pixel comparison, so per-frame tracking only commits state when the box
 * genuinely moved. The half-pixel tolerance absorbs the noise a fractional
 * layout produces without ever letting a visible offset through.
 */
export function sameAnchorRect(
  a: AnchorRect | null,
  b: AnchorRect | null,
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    Math.abs(a.top - b.top) < 0.5 &&
    Math.abs(a.left - b.left) < 0.5 &&
    Math.abs(a.width - b.width) < 0.5 &&
    Math.abs(a.height - b.height) < 0.5 &&
    a.radius === b.radius
  );
}

/**
 * Place the card beside its anchor, then clamp it into the viewport.
 *
 * Narrow windows routinely push the left and right placements past an edge. A
 * clamp pulls the card back in; flipping it to the far side would be a bigger
 * visual jump and can land it over the very control being explained.
 */
export function placeTourCard(
  rect: AnchorRect,
  side: TourSide,
  cardWidth: number,
  cardHeight: number,
  viewportWidth: number,
  viewportHeight: number,
  gap: number,
  edgePad: number,
): CardPlacement {
  let left: number;
  let top: number;

  if (side === "right") {
    left = rect.left + rect.width + gap;
    top = rect.top + rect.height / 2 - cardHeight / 2;
  } else if (side === "left") {
    left = rect.left - cardWidth - gap;
    top = rect.top + rect.height / 2 - cardHeight / 2;
  } else {
    left = rect.left + rect.width - cardWidth;
    top = rect.top + rect.height + gap;
  }

  left = Math.min(
    Math.max(left, edgePad),
    viewportWidth - cardWidth - edgePad,
  );
  top = Math.min(
    Math.max(top, edgePad),
    viewportHeight - cardHeight - edgePad,
  );

  return { top, left };
}
